import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  Divider,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Business,
  KeyboardArrowDown,
  Add,
  Check,
  Person,
  Settings,
  AccountBalance,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api, bankApi } from '@/services/api';
import { persistor } from '@/store';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { selectCurrentCompany, setAuthData } from '@/store/slices/authSlice';
import { selectBankAccount, selectAccount, setAccounts } from '@/store/slices/bankAccountSlice';
import type { BankAccountDTO } from '@/types/bank';
import type { Company as AuthCompany } from '@/types';
import { STORAGE_KEYS } from '@/utils/constants';
import toast from 'react-hot-toast';

interface Company {
  id: string;
  name: string;
  legalName: string;
  document: string;
  documentType: string;
  isDefault: boolean;
  isOwner: boolean;
  userRole: string;
}

/** Texto curto da conta: "Sicoob 213322" / "Efí 948866-9" */
export const bankAccountShortLabel = (acc: BankAccountDTO) =>
  `${acc.providerLabel} ${acc.accountNumber || acc.label}`;

const environmentLabel = (env: string | null) => {
  if (!env) return null;
  const e = env.toLowerCase();
  if (e === 'sandbox') return 'Sandbox';
  if (e === 'homologacao') return 'Homologação';
  return null; // production / producao: não polui o menu
};

/**
 * Seletor "Empresa · Banco".
 * Escolher uma CONTA = trocar de empresa (fluxo antigo: PUT /user/selected-company + setAuthData)
 * E de conta (PUT /bank/accounts/select + selectAccount). Empresa sem conta continua selecionável.
 */
const CompanySelector: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentCompany = useAppSelector(selectCurrentCompany);
  const selectedAccount = useAppSelector(selectBankAccount);

  // Ref para o efeito de montagem não usar uma seleção "velha" (closure) depois do await
  const selectedRef = useRef<BankAccountDTO | null>(selectedAccount);
  useEffect(() => {
    selectedRef.current = selectedAccount;
  }, [selectedAccount]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [accounts, setAccountList] = useState<BankAccountDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [changing, setChanging] = useState(false);

  const open = Boolean(anchorEl);

  // ---------------------------------------------------------------
  // Carregamento (empresas + contas de todas as empresas)
  // ---------------------------------------------------------------
  const loadAll = async (): Promise<{ companies: Company[]; accounts: BankAccountDTO[] }> => {
    setLoading(true);
    let comps: Company[] = [];
    let accs: BankAccountDTO[] = [];
    try {
      const [compRes, accRes] = await Promise.all([
        api.get('/user/companies').catch(() => null),
        bankApi.accountsAll().catch(() => null),
      ]);
      if (compRes?.success) {
        comps = compRes.data || [];
        setCompanies(comps);
      }
      if (accRes?.success) {
        accs = accRes.data || [];
        setAccountList(accs);
      }
    } catch (error) {
      console.error('Error loading companies/accounts:', error);
    } finally {
      setLoading(false);
    }
    return { companies: comps, accounts: accs };
  };

  /**
   * Seleção automática: mantém a conta selecionada se ela for da empresa atual (atualizando o DTO);
   * senão escolhe a `isDefault` (ou a primeira) da empresa atual e grava no backend.
   */
  const syncSelectionWithCompany = (all: BankAccountDTO[]) => {
    if (!currentCompany?.id) return;
    const mine = all.filter(a => a.companyId === currentCompany.id);
    dispatch(setAccounts(mine));

    const current = selectedRef.current;
    const stillValid =
      current && current.companyId === currentCompany.id
        ? mine.find(a => a.id === current.id) || null
        : null;

    if (stillValid) {
      dispatch(selectAccount(stillValid));
      api.setBankAccountId(stillValid.id);
      return;
    }

    const pick = mine.find(a => a.isDefault) || mine[0] || null;
    dispatch(selectAccount(pick));
    api.setBankAccountId(pick?.id ?? null);
    if (pick) {
      bankApi.select(pick.id).catch(err => console.warn('bank select (auto) falhou:', err));
    }
  };

  useEffect(() => {
    let cancelled = false;
    loadAll().then(({ accounts: accs }) => {
      if (!cancelled) syncSelectionWithCompany(accs);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    loadAll();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // ---------------------------------------------------------------
  // Troca de empresa (fluxo que já existia)
  // ---------------------------------------------------------------
  const applyCompany = async (company: Company): Promise<boolean> => {
    const response = await api.put('/user/selected-company', { companyId: company.id });
    if (!response?.success) return false;

    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    const user = userData ? JSON.parse(userData) : null;

    const newCompanyData = {
      id: company.id,
      name: company.name,
      document: company.document,
      tradingName: company.name,
      legalName: company.legalName,
    };

    localStorage.setItem(STORAGE_KEYS.COMPANY_DATA, JSON.stringify(newCompanyData));

    dispatch(setAuthData({
      user,
      // O objeto parcial é o que o login também guarda em COMPANY_DATA (mesmo formato de antes)
      company: newCompanyData as unknown as AuthCompany,
      token: localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN),
      refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
    }));
    return true;
  };

  const finishAndReload = async () => {
    handleClose();
    // Garante que o redux-persist gravou antes do reload (o interceptor lê do localStorage)
    try {
      await persistor.flush();
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  /** Escolheu uma CONTA (empresa · banco) */
  const handleSelectAccount = async (acc: BankAccountDTO) => {
    if (acc.id === selectedAccount?.id && acc.companyId === currentCompany?.id) {
      handleClose();
      return;
    }

    setChanging(true);
    try {
      if (acc.companyId !== currentCompany?.id) {
        const company = companies.find(c => c.id === acc.companyId);
        if (!company) {
          toast.error('Empresa da conta não encontrada');
          return;
        }
        const ok = await applyCompany(company);
        if (!ok) {
          toast.error('Erro ao alterar empresa');
          return;
        }
      }

      const selRes = await bankApi.select(acc.id);
      const dto = (selRes?.success && selRes.data) ? selRes.data : acc;

      dispatch(setAccounts(accounts.filter(a => a.companyId === acc.companyId)));
      dispatch(selectAccount(dto));
      api.setBankAccountId(dto.id);

      const companyName = dto.companyName || companies.find(c => c.id === dto.companyId)?.name;
      toast.success(`Conta alterada para ${companyName ? `${companyName} · ` : ''}${bankAccountShortLabel(dto)}`);
      await finishAndReload();
    } catch (error: any) {
      console.error('Error changing bank account:', error);
      toast.error(error?.error?.message || 'Erro ao alterar conta bancária');
    } finally {
      setChanging(false);
    }
  };

  /** Escolheu uma EMPRESA sem conta bancária */
  const handleSelectCompany = async (company: Company) => {
    if (company.id === currentCompany?.id && !selectedAccount) {
      handleClose();
      return;
    }

    setChanging(true);
    try {
      if (company.id !== currentCompany?.id) {
        const ok = await applyCompany(company);
        if (!ok) {
          toast.error('Erro ao alterar empresa');
          return;
        }
      }
      dispatch(setAccounts([]));
      dispatch(selectAccount(null));
      api.setBankAccountId(null);

      toast.success(`Empresa alterada para ${company.name}`);
      await finishAndReload();
    } catch (error: any) {
      console.error('Error changing company:', error);
      toast.error(error?.error?.message || 'Erro ao alterar empresa');
    } finally {
      setChanging(false);
    }
  };

  const formatDocument = (doc: string, type: string) => {
    if (!doc) return '';
    if (type === 'PF') {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  // ---------------------------------------------------------------
  // Agrupamento: empresas na ordem de /user/companies; contas de cada uma em seguida.
  // Contas de empresa que não veio na lista (edge) entram no fim, agrupadas por companyName.
  // ---------------------------------------------------------------
  type Group = { company: Company | null; companyId: string; companyName: string; accounts: BankAccountDTO[] };
  const groups: Group[] = companies.map(c => ({
    company: c,
    companyId: c.id,
    companyName: c.name,
    accounts: accounts.filter(a => a.companyId === c.id),
  }));
  const knownIds = new Set(companies.map(c => c.id));
  accounts
    .filter(a => !knownIds.has(a.companyId))
    .forEach(a => {
      let g = groups.find(x => x.companyId === a.companyId);
      if (!g) {
        g = { company: null, companyId: a.companyId, companyName: a.companyName || 'Empresa', accounts: [] };
        groups.push(g);
      }
      g.accounts.push(a);
    });

  const totalAccounts = accounts.length;
  const headerTitle = currentCompany?.name || 'Selecione';
  const headerText = selectedAccount
    ? `${headerTitle} · ${bankAccountShortLabel(selectedAccount)}`
    : headerTitle;

  const providerChip = (acc: BankAccountDTO) => (
    <Chip
      label={acc.providerLabel}
      size="small"
      variant="outlined"
      sx={{
        height: 18,
        fontSize: '0.6rem',
        fontWeight: 600,
        color: 'text.secondary',
        borderColor: alpha(theme.palette.text.primary, 0.2),
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.primary.main, 0.08),
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          textTransform: 'none',
          transition: 'all 0.2s',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
            borderColor: alpha(theme.palette.primary.main, 0.4),
          },
        }}
      >
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: alpha(theme.palette.primary.main, 0.15),
            color: 'primary.main',
          }}
        >
          {selectedAccount ? <AccountBalance sx={{ fontSize: 18 }} /> : <Business sx={{ fontSize: 18 }} />}
        </Avatar>
        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left' }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              fontSize: '0.65rem',
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Empresa · Banco
          </Typography>
          <Typography
            variant="body2"
            title={headerText}
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              lineHeight: 1.2,
              maxWidth: 260,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {headerText}
          </Typography>
        </Box>
        <KeyboardArrowDown
          sx={{
            color: 'text.secondary',
            transition: 'transform 0.2s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 360,
            maxHeight: 480,
            mt: 1,
            overflow: 'visible',
            boxShadow: theme.shadows[8],
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" fontWeight={600}>
            Empresas e contas bancárias
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {companies.length} empresa{companies.length !== 1 ? 's' : ''} · {totalAccounts} conta{totalAccounts !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {loading && groups.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : groups.length === 0 ? (
          <Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
            <Business sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Nenhuma empresa vinculada
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
            {groups.map((group, gi) => {
              const isCurrentCompany = group.companyId === currentCompany?.id;
              const docType = group.company?.documentType || 'PJ';
              const docText = group.company
                ? `${docType === 'PF' ? 'CPF' : 'CNPJ'}: ${formatDocument(group.company.document, docType)}`
                : '';

              return (
                <React.Fragment key={group.companyId}>
                  {gi > 0 && <Divider sx={{ my: 0.5 }} />}

                  {group.accounts.length === 0 ? (
                    // Empresa sem conta bancária: item selecionável
                    <MenuItem
                      onClick={() => group.company && handleSelectCompany(group.company)}
                      disabled={changing || !group.company}
                      sx={{
                        py: 1.25,
                        px: 2,
                        backgroundColor: isCurrentCompany && !selectedAccount
                          ? alpha(theme.palette.primary.main, 0.08)
                          : 'transparent',
                      }}
                    >
                      <ListItemIcon>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: isCurrentCompany ? 'primary.main' : alpha(theme.palette.text.primary, 0.1),
                            color: isCurrentCompany ? 'white' : 'text.secondary',
                          }}
                        >
                          {docType === 'PF' ? <Person sx={{ fontSize: 18 }} /> : <Business sx={{ fontSize: 18 }} />}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            fontWeight={isCurrentCompany ? 600 : 500}
                            sx={{
                              color: isCurrentCompany ? 'primary.main' : 'text.primary',
                              maxWidth: 240,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {group.companyName}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary" component="span">
                            {docText ? `${docText} · ` : ''}sem conta bancária configurada
                          </Typography>
                        }
                      />
                      {isCurrentCompany && !selectedAccount && <Check sx={{ color: 'primary.main', ml: 1 }} />}
                    </MenuItem>
                  ) : (
                    group.accounts.map(acc => {
                      const isSelected = isCurrentCompany && acc.id === selectedAccount?.id;
                      const envLabel = environmentLabel(acc.environment);
                      const secondaryParts: string[] = [];
                      if (docText) secondaryParts.push(docText);
                      if (acc.isDefault) secondaryParts.push('padrão');
                      if (!acc.configured) secondaryParts.push('não configurada');

                      return (
                        <MenuItem
                          key={acc.id}
                          onClick={() => handleSelectAccount(acc)}
                          disabled={changing}
                          sx={{
                            py: 1.25,
                            px: 2,
                            backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                            '&:hover': {
                              backgroundColor: isSelected
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.action.hover, 0.5),
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                bgcolor: isSelected ? 'primary.main' : alpha(theme.palette.text.primary, 0.1),
                                color: isSelected ? 'white' : 'text.secondary',
                              }}
                            >
                              <AccountBalanceWallet sx={{ fontSize: 18 }} />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="body2"
                                  fontWeight={isSelected ? 600 : 500}
                                  sx={{
                                    color: isSelected ? 'primary.main' : 'text.primary',
                                    maxWidth: 220,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {group.companyName} · {bankAccountShortLabel(acc)}
                                </Typography>
                                {providerChip(acc)}
                                {envLabel && (
                                  <Chip
                                    label={envLabel}
                                    size="small"
                                    sx={{
                                      height: 18,
                                      fontSize: '0.6rem',
                                      bgcolor: alpha(theme.palette.warning.main, 0.15),
                                      color: 'warning.dark',
                                    }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary" component="span">
                                {secondaryParts.join(' · ')}
                              </Typography>
                            }
                          />
                          {isSelected && <Check sx={{ color: 'primary.main', ml: 1 }} />}
                        </MenuItem>
                      );
                    })
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        )}

        <Divider />

        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/settings/contas');
          }}
          sx={{ py: 1.25 }}
        >
          <ListItemIcon>
            <AccountBalanceWallet fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Contas bancárias" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/admin/company');
          }}
          sx={{ py: 1.25 }}
        >
          <ListItemIcon>
            <Add fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Adicionar Empresa" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/settings/sicoob');
          }}
          sx={{ py: 1.25 }}
        >
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Configurar Sicoob" />
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClose();
            navigate('/settings/efi');
          }}
          sx={{ py: 1.25 }}
        >
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Configurar Efí" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default CompanySelector;
